import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getTelephonyStatus, telephonyApiUrl } from '../utils/telephony';

const POLL_MS = 20000;

/**
 * Pops up the calls the AI phone assistant answered (with the message the
 * caller left) for the users who are notified about them. Polls the
 * authenticated /api/telephony/ai/alerts endpoint, which returns each of the
 * caller's own notifications once; closing a card marks it read.
 */
export default function AiCallAlerts() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);

  useEffect(() => {
    let alive = true;
    let timer = null;

    const poll = async () => {
      try {
        const res = await axios.get(`${telephonyApiUrl}/api/telephony/ai/alerts`);
        const fresh = res.data || [];
        if (alive && fresh.length) setAlerts((as) => [...as, ...fresh].slice(-5));
      } catch (e) {
        // transient — try again next tick
      }
      if (alive) timer = setTimeout(poll, POLL_MS);
    };

    getTelephonyStatus().then((s) => {
      if (alive && s.enabled) poll();
    });
    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
    };
  }, []);

  if (!alerts.length) return null;

  const close = (id) => {
    setAlerts((as) => as.filter((a) => a.id !== id));
    axios.post(`${telephonyApiUrl}/api/telephony/ai/alerts/${id}/read`).catch(() => {});
  };

  return (
    <div className="fixed bottom-24 right-4 z-50 space-y-2 w-80 max-w-[calc(100vw-2rem)]" data-testid="ai-call-alerts">
      {alerts.map((a) => (
        <div key={a.id} role="alert" className="rounded-lg shadow-lg border border-violet-300 bg-white dark:bg-gray-800 p-3">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="text-xs font-semibold text-violet-700 dark:text-violet-300">🤖 {a.title}</div>
              <div className="text-sm text-gray-800 dark:text-gray-100 break-words">{a.message}</div>
            </div>
            <button type="button" onClick={() => close(a.id)} className="text-gray-400 hover:text-gray-600" aria-label={t('common.close')}>
              ✕
            </button>
          </div>
          {a.link && (
            <button
              type="button"
              onClick={() => {
                close(a.id);
                navigate(a.link);
              }}
              className="mt-2 text-sm text-blue-700 hover:underline"
            >
              {t('shell.aiCallAlerts.viewCall')} →
            </button>
          )}
        </div>
      ))}
    </div>
  );
}
